import { useI18n } from '../../i18n';
import { polar } from './geometry';

/**
 * A Western chart wheel showing the four major aspects as lines between
 * planets. Western vocabulary on purpose: this figure renders only inside the
 * explicitly Western Learn module, under its data-mode="western" subtree, so
 * every var(--mode-*) below arrives cool cyan.
 *
 * One planet (bottom of the wheel) acts as the hub — opposition, trine and
 * square all radiate from it, the way aspects actually pile onto one point in
 * a real chart — and a separate close pair shows a conjunction.
 */

const CX = 170;
const CY = 160;
const R = 120;

const HUB = 180; // bottom of the wheel
const OPPOSITION = 0; // 180° from the hub
const TRINE = 60; // 120° from the hub
const SQUARE = 270; // 90° from the hub
const CONJ_A = 300;
const CONJ_B = 310;

function dot(deg: number, key: string) {
  const [x, y] = polar(CX, CY, R, deg);
  return <circle key={key} cx={x} cy={y} r="5" fill="var(--mode)" stroke="none" />;
}

function chord(a: number, b: number, dash?: string) {
  const [x1, y1] = polar(CX, CY, R, a);
  const [x2, y2] = polar(CX, CY, R, b);
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke="var(--mode)"
      strokeWidth="1.5"
      strokeDasharray={dash}
    />
  );
}

export function AspectLines() {
  const { t } = useI18n();
  return (
    <svg
      viewBox="0 0 340 330"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.aspectLines.alt')}
    >
      <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--border-strong)" strokeWidth="1.5" />
      {/* Sign boundaries, kept quiet — the aspects are the subject here. */}
      {Array.from({ length: 12 }, (_, k) => {
        const [x1, y1] = polar(CX, CY, R - 6, k * 30);
        const [x2, y2] = polar(CX, CY, R, k * 30);
        return <line key={k} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--border)" strokeWidth="1" />;
      })}

      {chord(HUB, OPPOSITION)}
      {chord(HUB, TRINE, '6 4')}
      {chord(HUB, SQUARE, '2 3')}

      {dot(HUB, 'hub')}
      {dot(OPPOSITION, 'opp')}
      {dot(TRINE, 'trine')}
      {dot(SQUARE, 'square')}
      {dot(CONJ_A, 'conj-a')}
      {dot(CONJ_B, 'conj-b')}

      <text x={176} y={96} fontSize="8.5" fontWeight="600" fill="var(--mode-ink)">
        {t('learn.fig.aspectLines.opposition')}
      </text>
      <text x={230} y={196} fontSize="8.5" fontWeight="600" fill="var(--mode-ink)">
        {t('learn.fig.aspectLines.trine')}
      </text>
      <text x={104} y={224} textAnchor="end" fontSize="8.5" fontWeight="600" fill="var(--mode-ink)">
        {t('learn.fig.aspectLines.square')}
      </text>
      <text x={30} y={70} fontSize="8.5" fontWeight="600" fill="var(--mode-ink)">
        {t('learn.fig.aspectLines.conjunction')}
      </text>

      <text x={170} y={322} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.aspectLines.note')}
      </text>
    </svg>
  );
}
